import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchNewArrivalNotifications } from 'api/new-arrival-notification.service';
import { NewArrivalNotificationModel } from 'types/entity';
import { markASRead } from 'redux/new-arrival-notification-update/new-arrival-notification-update.slice';
import { loadFileDownload } from 'redux/file-download/file-download.slice';

export const NEW_ARRIVAL_NOTIFICATION_UNREAD_STORE_NAME = 'newArrivalNotificationUnreadStore';

export const loadUnreadNotifications = createAsyncThunk(
  `${NEW_ARRIVAL_NOTIFICATION_UNREAD_STORE_NAME}/load`,
  async ({ projectId, offset }: { projectId: string; offset: number }) => {
    const response = await fetchNewArrivalNotifications(projectId, offset, 'UNREAD');
    return response;
  },
);

export const loadMoreUnreadNotifications = createAsyncThunk(
  `${NEW_ARRIVAL_NOTIFICATION_UNREAD_STORE_NAME}/loadMore`,
  async ({ projectId, offset }: { projectId: string; offset: number }) => {
    const response = await fetchNewArrivalNotifications(projectId, offset, 'UNREAD');
    return response;
  },
);

export type NewArrivalNotificationData = NewArrivalNotificationModel & {
  /** Storeに入れる時に採番するインデックス番号 */
  id: number;
};

export type NewArrivalNotificationUnreadStoreState = {
  loaded: boolean;
  unreadNotifications: NewArrivalNotificationData[];
  currentOffset: number;
  hasMore: boolean;
  loadingMore: boolean;
  error: any;
};

const appendId = (data: NewArrivalNotificationModel[], offset: number): NewArrivalNotificationData[] => data.map((d, i) => ({ ...d, id: offset + i }));

export const initialNewArrivalNotificationUnreadStoreState: NewArrivalNotificationUnreadStoreState = {
  loaded: false,
  unreadNotifications: [],
  currentOffset: 0,
  hasMore: false,
  loadingMore: false,
  error: null,
};

export const newArrivalNotificationUnreadStoreSlice = createSlice({
  name: NEW_ARRIVAL_NOTIFICATION_UNREAD_STORE_NAME,
  initialState: initialNewArrivalNotificationUnreadStoreState,
  reducers: {
    resetState: (state) => {
      state.loaded = initialNewArrivalNotificationUnreadStoreState.loaded;
      state.unreadNotifications = initialNewArrivalNotificationUnreadStoreState.unreadNotifications;
      state.currentOffset = initialNewArrivalNotificationUnreadStoreState.currentOffset;
      state.hasMore = initialNewArrivalNotificationUnreadStoreState.hasMore;
      state.loadingMore = initialNewArrivalNotificationUnreadStoreState.loadingMore;
      state.error = initialNewArrivalNotificationUnreadStoreState.error;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadUnreadNotifications.pending, (state) => {
      state.loaded = false;
      state.error = null;
    });
    builder.addCase(loadUnreadNotifications.fulfilled, (state, action) => {
      state.unreadNotifications = appendId(action.payload.api_display_values || initialNewArrivalNotificationUnreadStoreState.unreadNotifications, 0);
      state.currentOffset = action.payload.offset || initialNewArrivalNotificationUnreadStoreState.currentOffset;
      state.hasMore = action.payload.additional_search_flag || false;
      state.loaded = true;
    });
    builder.addCase(loadUnreadNotifications.rejected, (state, action) => {
      state.error = action.payload;
      state.loaded = true;
    });
    builder.addCase(loadMoreUnreadNotifications.pending, (state) => {
      state.error = null;
      state.loadingMore = true;
    });
    builder.addCase(loadMoreUnreadNotifications.fulfilled, (state, action) => {
      const newItems = appendId(action.payload.api_display_values || initialNewArrivalNotificationUnreadStoreState.unreadNotifications, state.unreadNotifications.length);
      state.unreadNotifications = [...state.unreadNotifications, ...newItems];
      state.currentOffset = action.payload.offset || state.currentOffset;
      state.hasMore = action.payload.additional_search_flag ?? false;
      state.loadingMore = false;
    });
    builder.addCase(loadMoreUnreadNotifications.rejected, (state, action) => {
      state.error = action.payload;
    });
    builder.addCase(markASRead.fulfilled, (state, action) => {
      const ids = action.meta.arg.newArrivalNotificationRequest.new_arrival_notification_ids;
      const filteredItems = state.unreadNotifications?.filter(
        (v) => !ids.includes(v.new_arrival_notification_id || ''),
      ) || [];
      state.unreadNotifications = filteredItems;
    });
    builder.addCase(loadFileDownload.fulfilled, (state, action) => {
      const ids = action.meta.arg.fileDownloadAndNewArrivalNotificationRequest?.new_arrival_notification_ids || [];
      const filteredItems = state.unreadNotifications?.filter(
        (v) => !ids.includes(v.new_arrival_notification_id || ''),
      ) || [];
      state.unreadNotifications = filteredItems;
    });
  },
});

export const newArrivalNotificationUnreadStoreAction = {
  ...newArrivalNotificationUnreadStoreSlice.actions,
  loadUnreadNotifications,
  loadMoreUnreadNotifications,
};

export default newArrivalNotificationUnreadStoreSlice.reducer;
